// Static seed content for the public /portal-preview demo.
// IMPORTANT: this file must stay 100% static — no base44.entities calls, no
// auth. /portal-preview is a public, unauthenticated route, and everything
// rendered from this data must be safe to show to an anonymous visitor.
// Every number/label here was chosen to match the real Client Portal
// components it mirrors (OverviewModule, QualityOverviewTab / traceability
// chain copy, AIComplianceHub, ImplementationTracker) so the preview
// represents the product honestly rather than an idealized mockup.

export const WORKSPACE_PREVIEW = {
  labName: "Meridian Diagnostics Labs",
  welcomeMessage:
    "Your CAPA, deviation, and IQ/OQ/PQ documentation — drafted, tracked, and audit-ready in one place.",
  auditMirror: [
    { id: 1, actor: "AI Agent", action: "Drafted CAPA-2026-0182 (root cause + 3 corrective actions)", time: "2 min ago", tag: "ai" },
    { id: 2, actor: "Jamie R. (QA Manager)", action: "Approved and e-signed CAPA-2026-0182", time: "12 min ago", tag: "human" },
    { id: 3, actor: "AI Agent", action: "Flagged Gap #47 (ISO 17025 §7.3) — no linked evidence in 30 days", time: "1 hr ago", tag: "ai" },
    { id: 4, actor: "AI Agent", action: "Generated IQ/OQ/PQ package for Centrifuge Model X200", time: "3 hrs ago", tag: "ai" },
    { id: 5, actor: "Priya M. (Lab Director)", action: "Downloaded Q2 audit trail export (PDF, 214 records)", time: "Yesterday", tag: "human" },
  ],
  documents: [
    { id: "DOC-2201", name: "SOP-014 Sample Chain of Custody", status: "current", updated: "3 days ago" },
    { id: "DOC-2198", name: "CAPA-2026-0182 Root Cause Analysis", status: "in_review", updated: "12 min ago" },
    { id: "DOC-2190", name: "Deviation Report DEV-2026-041", status: "current", updated: "1 week ago" },
    { id: "DOC-2185", name: "IQ Protocol — Centrifuge X200", status: "current", updated: "2 weeks ago" },
  ],
};

export const QUALITY_PREVIEW = {
  tagline: "Requirement → Process → Control → Gap → CAPA, fully traceable.",
  chain: ["Requirement", "Process", "Control", "Gap", "CAPA"],
  stats: [
    { label: "Requirements tracked", value: "312", id: "quality-preview-requirements" },
    { label: "Processes mapped", value: "48", id: "quality-preview-processes" },
    { label: "Open gaps", value: "7", id: "quality-preview-gaps" },
    { label: "Open CAPAs", value: "4", id: "quality-preview-capa" },
  ],
  recentGaps: [
    { id: "GAP-047", requirement: "ISO 17025 §7.3 Sampling", process: "Sample Intake", status: "open", severity: "medium" },
    { id: "GAP-045", requirement: "21 CFR §11.10(e) Audit Trail", process: "LIMS Data Entry", status: "open", severity: "high" },
    { id: "GAP-041", requirement: "CLIA §493.1291 Reporting", process: "Result Release", status: "in_capa", severity: "medium" },
  ],
};

export const AI_TOOLS_PREVIEW = [
  { id: "ai-doc-workflow", name: "AI Document Workflow", desc: "Drafts CAPAs, deviations, and SOPs from a structured intake — every draft routes to a human reviewer before it's final.", metric: "60% faster drafting" },
  { id: "ai-batch-pdf", name: "Batch PDF Processor", desc: "Extracts and structures data from scanned lab records, calibration certs, and instrument printouts.", metric: "200+ docs/batch" },
  { id: "ai-deviation-pattern", name: "Deviation Pattern Analyzer", desc: "Surfaces recurring root causes across closed deviations so CAPAs address the pattern, not just the incident.", metric: "Trend detection" },
  { id: "ai-audit-readiness", name: "Audit Readiness Dashboard", desc: "Live score of how inspection-ready each process area is, with the specific gaps holding the score down.", metric: "97.8% avg. score" },
];

export const IMPLEMENTATION_PREVIEW = {
  phases: [
    { key: "discovery", label: "Discovery", done: true },
    { key: "setup", label: "Setup", done: true },
    { key: "build", label: "Build", done: true },
    { key: "validate", label: "Validate", done: false, current: true },
    { key: "launch", label: "Launch", done: false },
  ],
  metrics: {
    hoursSavedPerMonth: 34,
    qaHourlyRate: 85,
    docsCompleted: 61,
    monthlySavings: 34 * 85,
  },
  templates: [
    { id: "tpl-1", name: "CAPA Root Cause Prompt", type: "prompt" },
    { id: "tpl-2", name: "Deviation Intake Template", type: "document" },
    { id: "tpl-3", name: "IQ/OQ/PQ Protocol Prompt", type: "prompt" },
  ],
};

// Scripted guided-tour steps for the Demo Navigator. No live agent/LLM call
// on this public route — a fixed script keeps the public-facing surface
// free of unmetered AI cost/abuse exposure while still demonstrating the
// real Navigator's pointer/spotlight mechanic (see DemoNavigatorSpotlight.jsx).
export const TOUR_STEPS = [
  {
    id: "intro",
    tab: null,
    targetId: null,
    title: "Welcome to LabComplyx",
    text: "This is a live preview of the Client Portal, using sample data. Click Next and I'll walk you through it the way I'd walk a new client through it.",
  },
  {
    id: "workspace",
    tab: "workspace",
    targetId: "demo-tab-workspace",
    title: "Workspace",
    text: "Every project starts here. The welcome banner and Audit Mirror show a live feed of every action taken — by the AI or by your team — nothing happens off the record.",
  },
  {
    id: "quality",
    tab: "quality",
    targetId: "demo-tab-quality",
    title: "Quality Overview",
    text: "This is the traceability spine: Requirement → Process → Control → Gap → CAPA. Every open gap is linked all the way back to the regulatory requirement that created it.",
  },
  {
    id: "ai-tools",
    tab: "ai-tools",
    targetId: "demo-tab-ai-tools",
    title: "Compliance Center — AI Tools",
    text: "This is where the 60% time savings comes from: CAPAs, deviations, and IQ/OQ/PQ docs get drafted automatically — always landing at a human sign-off gate before anything is final.",
  },
  {
    id: "implementation",
    tab: "implementation",
    targetId: "demo-tab-implementation",
    title: "Implementation Tracker",
    text: "This is what your onboarding looks like — phase-by-phase status, plus the ROI you're actually capturing each month, calculated from real QA hourly rates.",
  },
  {
    id: "end",
    tab: null,
    targetId: null,
    title: "That's the core loop",
    text: "Everything you just saw runs on your own data once you're onboarded, with the same human-review gate on every AI action. Ready to see it on your data?",
  },
];
